import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  LobbyPayload,
  OnlineClientMessage,
  OnlineGameMessage,
  OnlineGameSnapshot,
  ReplayDetail,
  ReplaySummary,
} from '@ccc/shared-types';

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const message =
      data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
        ? data.message
        : 'That did not work. Try again.';
    throw new Error(message);
  }
  return data as T;
}

export function useOnlineGame(gameId: string) {
  const [snapshot, setSnapshot] = useState<OnlineGameSnapshot | null>(null);
  const [status, setStatus] = useState<'connecting' | 'online' | 'reconnecting' | 'offline'>(
    'connecting',
  );
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number | null>(null);

  useEffect(() => {
    let closedByEffect = false;

    async function loadInitial() {
      try {
        const data = await apiJson<OnlineGameSnapshot>(`/api/games/${gameId}`);
        if (!closedByEffect) setSnapshot(data);
      } catch (err) {
        if (!closedByEffect) setError(err instanceof Error ? err.message : 'Could not load game.');
      }
    }

    function connect(delay = 0) {
      if (reconnectRef.current) window.clearTimeout(reconnectRef.current);
      reconnectRef.current = window.setTimeout(() => {
        if (closedByEffect) return;
        setStatus((prev) => (prev === 'connecting' ? 'connecting' : 'reconnecting'));
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const socket = new WebSocket(`${protocol}//${window.location.host}/ws/game/${gameId}`);
        socketRef.current = socket;
        socket.addEventListener('open', () => {
          setStatus('online');
          setError(null);
          socket.send(JSON.stringify({ kind: 'hello' }));
        });
        socket.addEventListener('message', (event) => {
          const msg = JSON.parse(String(event.data)) as OnlineGameMessage;
          if (msg.kind === 'state' && msg.state) setSnapshot(msg.state);
          if (msg.kind === 'error') setError(msg.message ?? msg.error ?? 'The server rejected that.');
        });
        socket.addEventListener('close', () => {
          if (closedByEffect) return;
          setStatus('offline');
          if (snapshot?.state?.status !== 'ended') connect(900);
        });
        socket.addEventListener('error', () => {
          if (!closedByEffect) setStatus('offline');
        });
      }, delay);
    }

    void loadInitial();
    connect();

    return () => {
      closedByEffect = true;
      if (reconnectRef.current) window.clearTimeout(reconnectRef.current);
      socketRef.current?.close();
    };
  }, [gameId]);

  const send = useCallback((message: OnlineClientMessage) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setError('Reconnecting. Try again in a moment.');
      return;
    }
    socket.send(JSON.stringify(message));
  }, []);

  return { snapshot, status, error, send };
}

export async function loadLobby(): Promise<LobbyPayload> {
  return apiJson<LobbyPayload>('/api/lobby');
}

export async function loadReplays(query: string): Promise<{ replays: ReplaySummary[]; page: number }> {
  return apiJson(`/api/replays${query}`);
}

export async function loadReplay(gameId: string): Promise<ReplayDetail> {
  const data = await apiJson<{ replay: ReplayDetail }>(`/api/games/${gameId}/replay`);
  return data.replay;
}
